/**
 * @file meetingListScope.ts
 * @brief "Mine | Team" on the meeting lists, company admins only.
 *
 * @details
 * DES 21-10-2026: an admin's lists mixed their own meetings with every
 * member's, so admins get a switch on Boardrooms, Notes (Meeting notes) and
 * the Home meeting rail. Default Mine, remembered on this device (one choice
 * for every list, so they never disagree). Members and personal accounts
 * send no scope and see no switch: their lists are unchanged.
 *
 * Server (BACKEND b2c5fad): `GET /api/boardroom/sessions?scope=mine|team`.
 * mine = own + shared with me + invited to; team = meetings owned by other
 * members of my company. The reply's `teamScopeAvailable` is true for company
 * admins; a row I don't own carries `owner: { userId, firstName, name,
 * avatarUrl }`. Ask and search are not scoped.
 *
 * @references scratch/claude-code-report.md "22-10-2026 BACKEND" section 2
 *
 * @author DES
 * @date 22-10-2026
 */

import { useSyncExternalStore } from "react";
import { useAuth } from "@/shared/auth/auth-context";
import { isCompanyAdminRole } from "@/shared/auth/homePath";

export type MeetingListScope = "mine" | "team";

const KEY = "verblizr.meetingListScope";
const listeners = new Set<() => void>();

function read(): MeetingListScope {
  try {
    return window.localStorage.getItem(KEY) === "team" ? "team" : "mine";
  } catch {
    return "mine";
  }
}

let current: MeetingListScope | null = null;

function snapshot(): MeetingListScope {
  if (current === null) current = typeof window === "undefined" ? "mine" : read();
  return current;
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function setMeetingListScope(next: MeetingListScope) {
  current = next;
  try {
    window.localStorage.setItem(KEY, next);
  } catch {
    // Storage blocked: the choice lasts for this page load.
  }
  for (const fn of listeners) fn();
}

/**
 * `scope` is what to send (undefined for anyone who is not a company admin:
 * send nothing); `available` says whether to show the switch.
 */
export function useMeetingListScope(): {
  available: boolean;
  scope: MeetingListScope | undefined;
  setScope: (next: MeetingListScope) => void;
} {
  const { user } = useAuth();
  const stored = useSyncExternalStore(subscribe, snapshot, () => "mine" as const);
  const available = isCompanyAdminRole(user?.role);
  return { available, scope: available ? stored : undefined, setScope: setMeetingListScope };
}

/** Empty Team state (DES). */
export const TEAM_EMPTY_TITLE = "No team meetings yet.";

/** "Ali's meeting" for a team row's owner. */
export function ownerMeetingLabel(owner: { firstName?: string | null; name?: string | null }): string {
  const first = owner.firstName?.trim() || owner.name?.trim().split(/\s+/)[0] || "";
  return first ? `${first}'s meeting` : "A colleague's meeting";
}
